import React, { useEffect, useRef } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';

import { ITooltip } from '@components/Tooltip';
import ProductDefaultImg from '@assets/svg/camera-default.svg';
import Rolling from '@assets/gif/rolling.gif';

import { IManufacturer } from '@models/Manufacturer.model';
import { useInView } from 'react-intersection-observer';
import { Shimmer } from '@components/index';
import {
  InputDropDownWrapper,
  Suggested,
  Quote,
  Suggestions,
  Suggestion,
  Left,
  ImagePlaceholder,
  Image,
  ProductName,
  ProductToolTip,
  ProductToolTipText,
  LoadMoreContainer,
  LoadMore,
  LoadingText,
  LoadingImg,
} from './InputDropDown.styles';

const useOutsideClick = (callback: () => void) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: Event) => {
      if (ref.current && !ref.current?.contains(event.target as Node)) {
        callback && callback();
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  return ref;
};

interface IInputDropDown {
  searchedValue?: string;
  onClose?: any;
  manufacturers: IManufacturer[];
  hasMoreManufacturers: boolean;
  loadMoreManufacturers: () => void;
  isLoading: boolean;
  setManufacturer: (m: IManufacturer) => void;
}

export const InputDropDown = ({
  searchedValue,
  onClose,
  manufacturers,
  hasMoreManufacturers,
  loadMoreManufacturers,
  isLoading,
  setManufacturer,
}: IInputDropDown) => {
  const ref = useOutsideClick(onClose);

  const manufacturersInView = useInView();

  useEffect(() => {
    if (manufacturersInView.inView && hasMoreManufacturers)
      loadMoreManufacturers();
  }, [manufacturersInView.inView, hasMoreManufacturers]);

  return (
    <InputDropDownWrapper ref={ref}>
      <Suggested onClick={onClose}>
        Use <Quote>{searchedValue}</Quote>
      </Suggested>

      <Suggestions>
        {isLoading && (
          <>
            <Shimmer
              style={{ height: 42, width: '100%', borderRadius: 4 }}
              primary="#f1f1f1"
              secondary="#ececec"
            />
            <Shimmer
              style={{
                height: 42,
                width: '100%',
                borderRadius: 4,
                marginTop: 4,
              }}
              primary="#f1f1f1"
              secondary="#ececec"
            />
          </>
        )}
        {manufacturers.map(item => (
          <Suggestion key={item.id} onClick={() => setManufacturer(item)}>
            <Left>
              <ImagePlaceholder>
                <Image
                  src={item.imageUrl || ProductDefaultImg}
                  brokenSrc={ProductDefaultImg}
                />
              </ImagePlaceholder>
              <ProductName>{item.name}</ProductName>
            </Left>
            {item.status === 'internal' && (
              <ProductToolTip data-for={item.name} data-tip={item.name}>
                <BlueIcon icon="issue" color="#8F95B2" size={12} />
                <ITooltip
                  id={item.name}
                  getContent={() => {
                    return (
                      <ProductToolTipText>
                        Internal Manufacturer
                      </ProductToolTipText>
                    );
                  }}
                />
              </ProductToolTip>
            )}
          </Suggestion>
        ))}
        <LoadMoreContainer ref={manufacturersInView.ref}>
          {hasMoreManufacturers ? (
            <LoadMore>
              <LoadingText>Loading more</LoadingText>
              <LoadingImg src={Rolling} alt="loading" />
            </LoadMore>
          ) : (
            <div />
          )}
        </LoadMoreContainer>
      </Suggestions>
    </InputDropDownWrapper>
  );
};
